import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import {
  useFieldArray,
  useForm,
  type SubmitHandler,
} from 'react-hook-form';
import {
  ArrowDownToLine,
  Calendar,
  ChevronRight,
  CreditCard,
  Eye,
  FileText,
  Pencil,
  Percent,
  Plus,
  Tag,
  Trash2,
  User2,
  X,
} from 'lucide-react';
import { useActiveOrganization } from '@repo/api-client/react';
import { useClients, useCreateService, useItems, useTaxOptions } from '@repo/queries';
import {
  Money,
  computeInvoiceTotals,
  getCurrencyScale,
  type DiscountConfig,
  type LineItemInput,
} from '@repo/domain';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { Client, Service, TaxOption } from '@repo/api-client/dtos';
import { displayWireMoney, parseUserMoney } from '@/features/money';
import { ClientAvatar, inferClientTag } from '@/features/avatar';
import { cn } from '@/lib/utils';
import {
  buildCreateInvoiceRequest,
  defaultPaymentMethodLabel,
  emptyLineItem,
  formTaxToWire,
  invoiceFormDefaults,
  invoiceFormSchema,
  minorToDecimal,
  type FormDiscount,
  type InvoiceFormValues,
  type InvoiceLineItemFormValues,
} from './invoiceFormSchema';
import { DiscountDialog } from './DiscountDialog';
import { PaymentMethodsDialog } from './PaymentMethodsDialog';
import { TaxDialog, type TaxValue } from './TaxDialog';
import { TaxMutexDialog } from './TaxMutexDialog';
import { LineItemDialog, type LineItemDraft } from './LineItemDialog';
import { ServicePickerDialog } from './ServicePickerDialog';
import { ClientPickerDialog } from './ClientPickerDialog';
import { QuickAddClientDialog } from './QuickAddClientDialog';
import { InvoicePreviewDialog } from './InvoicePreviewDialog';
import { buildPreviewInvoice } from './buildPreviewInvoice';

interface InvoiceFormProps {
  defaultValues?: InvoiceFormValues;
  defaultCurrency: string;
  onSubmit: (
    payload: ReturnType<typeof buildCreateInvoiceRequest>,
  ) => Promise<void> | void;
  onCancel?: () => void;
  loading?: boolean;
  submitLabel?: string;
  /** Optional invoice number, surfaced in the slim header status line. */
  invoiceNumber?: string;
}

/**
 * Document-style invoice composer.
 *
 * The previous shape was a vertical stack of "Section" cards labelled
 * Header / Line items / Tax / Notes — the user's exact words on
 * 2026-05-10 were "I HATE these forms". The redesign mirrors what an
 * invoice actually looks like:
 *
 *   - Top: a single canvas (the "page") with the org on the left and
 *     the invoice meta — number, dates — typeset on the right. Bill-
 *     to sits below as a recipient block.
 *   - Middle: line items as a real table with subtle column headers
 *     and a per-row total cell, NOT a stack of card-shaped editors.
 *   - Right: a totals box pinned alongside the table so the running
 *     amount is always in the user's eye.
 *   - Footer: notes / terms / signature inline (no boxes).
 *
 * Tax controls are tucked into a "Tax & rounding" disclosure under
 * the totals so the common path (no tax / one rate) stays one click.
 *
 * Money discipline:
 *   - Users type decimals into `unitPriceInput`. We lift via
 *     `parseUserMoney` per line on submit and serialise to the
 *     wire's minor-units string.
 *   - Live totals reuse `@repo/domain.computeInvoiceTotals` so the
 *     preview number == the wire number on the resulting row.
 */
export function InvoiceForm({
  defaultValues,
  defaultCurrency,
  onSubmit,
  onCancel,
  loading,
  submitLabel = 'Save invoice',
  invoiceNumber,
}: InvoiceFormProps) {
  const { clients } = useClients();
  const { items: services } = useItems();
  const { taxOptions } = useTaxOptions();
  const { createService } = useCreateService();
  const org = useActiveOrganization();
  const businessName = org?.nickname || org?.name || 'Your business';
  const [lineDiscountIdx, setLineDiscountIdx] = useState<number | null>(null);
  const [invoiceDiscountOpen, setInvoiceDiscountOpen] = useState(false);
  const [paymentMethodsOpen, setPaymentMethodsOpen] = useState(false);
  const [lineTaxIdx, setLineTaxIdx] = useState<number | null>(null);
  const [invoiceTaxOpen, setInvoiceTaxOpen] = useState(false);
  const [taxMutexMessage, setTaxMutexMessage] = useState<string | null>(null);
  // `lineEditIdx` is the row being edited; `null` means closed. `'new'`
  // distinguishes the "+ New item" path so the dialog surfaces its
  // "save to library" affordance.
  const [lineEditIdx, setLineEditIdx] = useState<number | 'new' | null>(null);
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [quickAddClientOpen, setQuickAddClientOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: defaultValues ?? invoiceFormDefaults(defaultCurrency),
  });

  const {
    fields,
    append,
    remove,
  } = useFieldArray({ control: form.control, name: 'lineItems' });

  const values = form.watch();
  const totals = useLiveTotals(values);
  const errors = form.formState.errors;

  // ---- Tax UX helpers ----------------------------------------------------
  // The wire's TaxConfig is a single mode (per_item OR on_total OR
  // as_deduction OR none), so we infer the right mode from which
  // affordance the user just touched. Mutex check prevents the modes
  // from contradicting each other.

  const anyLineHasTax = values.lineItems.some(
    (l) => l.taxRate && l.taxRate.trim() !== '',
  );

  function attemptOpenLineTax(idx: number) {
    if (values.tax.method === 'on_total' || values.tax.method === 'as_deduction') {
      setTaxMutexMessage(
        'This invoice has a single tax on the total. Remove it first to add per-line taxes.',
      );
      return;
    }
    setLineTaxIdx(idx);
  }

  function attemptOpenInvoiceTax() {
    if (values.tax.method === 'per_item' && anyLineHasTax) {
      setTaxMutexMessage(
        'This invoice has per-line taxes. Remove them first to add one tax on the total.',
      );
      return;
    }
    setInvoiceTaxOpen(true);
  }

  function applyLineTax(idx: number, next: TaxValue | null) {
    if (next === null) {
      // Clear this row; if no other row has a rate, drop back to none.
      form.setValue(`lineItems.${idx}.taxRate` as const, undefined, {
        shouldDirty: true,
      });
      const others = values.lineItems.some(
        (l, i) => i !== idx && l.taxRate && l.taxRate.trim() !== '',
      );
      if (!others) {
        form.setValue('tax', { method: 'none' }, { shouldDirty: true });
      }
      return;
    }
    form.setValue(`lineItems.${idx}.taxRate` as const, next.rate, {
      shouldDirty: true,
    });
    form.setValue(`lineItems.${idx}.taxable` as const, true, {
      shouldDirty: true,
    });
    if (values.tax.method !== 'per_item') {
      form.setValue(
        'tax',
        { method: 'per_item', inclusive: false, label: next.label },
        { shouldDirty: true },
      );
    } else if (values.tax.label !== next.label) {
      // Keep the invoice-level label in sync with the most recent
      // line tax — small thing, but the tax line in the PDF reads
      // that label and a stale "Tax" looks careless.
      form.setValue(
        'tax',
        { method: 'per_item', inclusive: values.tax.inclusive, label: next.label },
        { shouldDirty: true },
      );
    }
  }

  function applyInvoiceTax(next: TaxValue | null) {
    if (next === null) {
      form.setValue('tax', { method: 'none' }, { shouldDirty: true });
      return;
    }
    if (next.kind === 'subtract') {
      form.setValue(
        'tax',
        { method: 'as_deduction', rate: next.rate, label: next.label },
        { shouldDirty: true },
      );
    } else {
      form.setValue(
        'tax',
        {
          method: 'on_total',
          rate: next.rate,
          inclusive: false,
          label: next.label,
        },
        { shouldDirty: true },
      );
    }
  }

  function clearInvoiceTax() {
    form.setValue('tax', { method: 'none' }, { shouldDirty: true });
  }

  // ---- Line item dialog wiring ------------------------------------------

  const lineEditInitial: LineItemDraft | null =
    typeof lineEditIdx === 'number'
      ? (() => {
          const line = values.lineItems[lineEditIdx];
          if (!line) return null;
          return {
            description: line.description,
            quantity: line.quantity,
            unitPriceInput: line.unitPriceInput,
            unit: line.unit,
          };
        })()
      : null;

  function applyLineDialog(
    draft: LineItemDraft,
    opts: { saveToLibrary: boolean },
  ) {
    if (lineEditIdx === null) return;
    if (lineEditIdx === 'new') {
      // New lines default to taxable=true; the per-line tax mutex
      // means the toggle is redundant under invoice-wide tax modes.
      append({
        ...emptyLineItem(),
        description: draft.description,
        quantity: draft.quantity,
        unitPriceInput: draft.unitPriceInput,
        unit: draft.unit,
      });
    } else {
      // Preserve any per-line discount / taxRate / notes / taxable
      // flag the row already carries — the dialog doesn't manage
      // those (notes & taxable are not user-editable on web).
      const existing = values.lineItems[lineEditIdx];
      const next: InvoiceLineItemFormValues = {
        ...(existing ?? emptyLineItem()),
        description: draft.description,
        quantity: draft.quantity,
        unitPriceInput: draft.unitPriceInput,
        unit: draft.unit,
      };
      form.setValue(`lineItems.${lineEditIdx}` as const, next, {
        shouldDirty: true,
      });
    }
    if (opts.saveToLibrary) {
      // Fire-and-forget — failure shouldn't block the user from
      // continuing with their invoice.
      const parsed = parseUserMoney(draft.unitPriceInput, values.currency);
      if (parsed.ok) {
        void createService({
          name: draft.description,
          pricingRate: parsed.value.amount.toString(),
          pricingCurrency: values.currency,
          pricingUnit: draft.unit,
          taxable: true,
          tags: [],
          isActive: true,
        }).catch(() => {
          /* swallow — user can re-create later from settings if it fails */
        });
      }
    }
  }

  function appendLineFromService(service: Service) {
    append({
      ...emptyLineItem(),
      description: service.name,
      quantity: '1',
      unitPriceInput: minorToDecimal(service.pricingRate, service.pricingCurrency),
      unit: service.pricingUnit,
      taxable: service.taxable,
      notes: '',
    });
  }

  // ----------------------------------------------------------------------

  const onValid: SubmitHandler<InvoiceFormValues> = async (formValues) => {
    const lineUnitPriceMinor: string[] = [];
    let lineErrored = false;
    for (let i = 0; i < formValues.lineItems.length; i += 1) {
      const line = formValues.lineItems[i]!;
      const result = parseUserMoney(line.unitPriceInput, formValues.currency);
      if (!result.ok) {
        form.setError(`lineItems.${i}.unitPriceInput`, {
          message: 'Enter a valid amount',
        });
        lineErrored = true;
        continue;
      }
      lineUnitPriceMinor.push(result.value.amount.toString());
    }
    if (lineErrored) return;
    const payload = buildCreateInvoiceRequest({
      values: formValues,
      lineUnitPriceMinor,
    });
    await onSubmit(payload);
  };

  const selectedClient = (clients ?? []).find((c) => c.id === values.clientId);
  const selectedClientEmail =
    selectedClient && typeof selectedClient.contact === 'object' && selectedClient.contact !== null
      ? (selectedClient.contact as Record<string, unknown>)['email']
      : undefined;
  const selectedClientEmailText =
    typeof selectedClientEmail === 'string' && selectedClientEmail.length > 0
      ? selectedClientEmail
      : null;

  return (
    <form
      onSubmit={form.handleSubmit(onValid)}
      className="flex flex-col gap-6"
      noValidate
    >
      {/* The "page" — single canvas that visually echoes the rendered
          invoice so the form feels like editing a document, not
          filling a tax form. */}
      <article className="overflow-hidden rounded-2xl border border-line bg-card">
        <header className="flex flex-col gap-3 border-b border-line bg-secondary/30 px-6 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span className="text-foreground">{businessName}</span>
            <span className="mx-2 font-normal text-muted-foreground/60">·</span>
            <span className="font-mono">{values.currency}</span>
            <span className="mx-2 font-normal text-muted-foreground/60">·</span>
            <span>{invoiceNumber ? `Editing ${invoiceNumber}` : 'New invoice'}</span>
          </p>
          <BillToButton
            client={selectedClient ?? null}
            emailText={selectedClientEmailText}
            onClick={() => setClientPickerOpen(true)}
            invalid={!!errors.clientId}
          />
          <DateStrip
            issueDate={values.issueDate}
            dueDate={values.dueDate}
            onIssueChange={(v) => form.setValue('issueDate', v, { shouldDirty: true })}
            onDueChange={(v) => form.setValue('dueDate', v, { shouldDirty: true })}
            issueError={errors.issueDate?.message}
            dueError={errors.dueDate?.message}
          />
          {errors.clientId?.message ? (
            <p className="text-xs text-destructive">{errors.clientId.message}</p>
          ) : null}
        </header>

        <div className="px-6 py-5">
          {fields.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-line bg-secondary/20 px-6 py-10 text-center">
              <p className="text-sm text-muted-foreground">No items yet.</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  type="button"
                  onClick={() => setServicePickerOpen(true)}
                  leadingIcon={<Plus className="size-4" />}
                >
                  Add service
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLineEditIdx('new')}
                  leadingIcon={<Plus className="size-4" />}
                >
                  New item
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {fields.map((field, idx) => (
                  <LineRow
                    key={field.id}
                    idx={idx}
                    line={values.lineItems[idx]!}
                    currency={values.currency}
                    lineTotal={totals.perLine[idx] ?? '0'}
                    canRemove={fields.length > 1}
                    onRemove={() => remove(idx)}
                    onEdit={() => setLineEditIdx(idx)}
                    onOpenDiscount={() => setLineDiscountIdx(idx)}
                    onOpenTax={() => attemptOpenLineTax(idx)}
                    taxMethod={values.tax.method}
                  />
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setServicePickerOpen(true)}
                  leadingIcon={<Plus className="size-4" />}
                >
                  Add service
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setLineEditIdx('new')}
                  leadingIcon={<Plus className="size-4" />}
                  className="text-muted-foreground hover:text-foreground"
                >
                  New item
                </Button>
              </div>
            </>
          )}
          {errors.lineItems?.message ? (
            <p className="mt-2 text-xs text-destructive">
              {errors.lineItems.message}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-6 border-t border-line px-6 py-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex flex-col gap-4">
            <NotesField
              id="notes"
              label="Notes"
              hint="Visible to the client. Anything they should see when they open the invoice."
              register={form.register('notes')}
            />
            <NotesField
              id="terms"
              label="Terms"
              hint="Payment terms, late fees, refund policy."
              register={form.register('terms')}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InlineField
                label="Signature"
                value={values.signature ?? ''}
                error={errors.signature?.message}
              >
                <Input {...form.register('signature')} />
              </InlineField>
            </div>
          </div>

          <aside className="rounded-xl border border-line bg-secondary/30 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Totals
            </p>
            <dl className="mt-3 space-y-1.5 text-sm">
              <SummaryRow
                label="Subtotal"
                value={displayWireMoney(totals.subtotal, values.currency)}
              />
              <DiscountSummaryRow
                discount={values.invoiceDiscount}
                discountTotal={totals.discountTotal}
                currency={values.currency}
                onOpen={() => setInvoiceDiscountOpen(true)}
                onClear={() =>
                  form.setValue('invoiceDiscount', undefined, { shouldDirty: true })
                }
              />
              <TaxSummaryRow
                tax={values.tax}
                taxTotal={totals.taxTotal}
                currency={values.currency}
                onOpen={() => attemptOpenInvoiceTax()}
                onClear={() => clearInvoiceTax()}
              />
            </dl>
            <div className="mt-3 flex items-baseline justify-between border-t border-line pt-3">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Total
              </span>
              <span className="font-display text-2xl font-semibold tabular-nums text-foreground">
                {displayWireMoney(totals.total, values.currency)}
              </span>
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <PaymentMethodsSummary
                methods={values.paymentMethods ?? []}
                onOpen={() => setPaymentMethodsOpen(true)}
              />
            </div>
          </aside>
        </div>
      </article>

      {/* Modal dialogs — rendered once at the form root so the same
          instance handles per-line and invoice-level discounts. */}
      <DiscountDialog
        open={lineDiscountIdx !== null}
        onOpenChange={(next) => {
          if (!next) setLineDiscountIdx(null);
        }}
        initial={
          lineDiscountIdx !== null
            ? values.lineItems[lineDiscountIdx]?.discount
            : undefined
        }
        currency={values.currency}
        title="Line discount"
        description="Applies to this row only. Stacks with the invoice-level discount."
        onSave={(next) => {
          if (lineDiscountIdx === null) return;
          form.setValue(
            `lineItems.${lineDiscountIdx}.discount` as const,
            next ?? undefined,
            { shouldDirty: true },
          );
        }}
      />
      <DiscountDialog
        open={invoiceDiscountOpen}
        onOpenChange={setInvoiceDiscountOpen}
        initial={values.invoiceDiscount}
        currency={values.currency}
        title="Invoice discount"
        description="Applied to the subtotal after per-line discounts, before tax."
        onSave={(next) => {
          form.setValue('invoiceDiscount', next ?? undefined, {
            shouldDirty: true,
          });
        }}
      />
      <PaymentMethodsDialog
        open={paymentMethodsOpen}
        onOpenChange={setPaymentMethodsOpen}
        initial={values.paymentMethods ?? []}
        onSave={(next) =>
          form.setValue('paymentMethods', next, { shouldDirty: true })
        }
      />
      <TaxDialog
        open={lineTaxIdx !== null}
        onOpenChange={(next) => {
          if (!next) setLineTaxIdx(null);
        }}
        initial={
          lineTaxIdx !== null
            ? lineTaxToTaxValue(values, lineTaxIdx)
            : null
        }
        level="line"
        presets={taxOptions as TaxOption[] | undefined}
        onSave={(next) => {
          if (lineTaxIdx === null) return;
          applyLineTax(lineTaxIdx, next);
        }}
      />
      <TaxDialog
        open={invoiceTaxOpen}
        onOpenChange={setInvoiceTaxOpen}
        initial={invoiceTaxToTaxValue(values.tax)}
        level="invoice"
        presets={taxOptions as TaxOption[] | undefined}
        onSave={applyInvoiceTax}
      />
      <TaxMutexDialog
        open={taxMutexMessage !== null}
        onOpenChange={(next) => {
          if (!next) setTaxMutexMessage(null);
        }}
        message={taxMutexMessage ?? ''}
      />
      <LineItemDialog
        open={lineEditIdx !== null}
        onOpenChange={(next) => {
          if (!next) setLineEditIdx(null);
        }}
        initial={lineEditInitial}
        currency={values.currency}
        allowSaveToLibrary={lineEditIdx === 'new'}
        onSave={applyLineDialog}
      />
      <ServicePickerDialog
        open={servicePickerOpen}
        onOpenChange={setServicePickerOpen}
        services={services ?? []}
        onPick={appendLineFromService}
        onCreateNew={() => {
          setServicePickerOpen(false);
          setLineEditIdx('new');
        }}
      />
      <ClientPickerDialog
        open={clientPickerOpen}
        onOpenChange={setClientPickerOpen}
        clients={clients ?? []}
        selectedId={values.clientId}
        onPick={(client) =>
          form.setValue('clientId', client.id, { shouldDirty: true, shouldValidate: true })
        }
        onCreateNew={() => {
          setClientPickerOpen(false);
          setQuickAddClientOpen(true);
        }}
      />
      <QuickAddClientDialog
        open={quickAddClientOpen}
        onOpenChange={setQuickAddClientOpen}
        onCreated={(client) =>
          form.setValue('clientId', client.id, { shouldDirty: true, shouldValidate: true })
        }
      />
      <InvoicePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        invoice={buildPreviewInvoice({
          values,
          totals: {
            subtotal: totals.subtotal,
            discountTotal: totals.discountTotal,
            taxTotal: totals.taxTotal,
            total: totals.total,
          },
          invoiceNumber,
          client: selectedClient ?? null,
        })}
        organization={org}
        client={selectedClient ?? null}
      />

      <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-line bg-background/85 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-xl sm:border sm:px-4">
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileText className="size-3.5" />
          Saving as <span className="font-medium text-foreground">draft</span> —
          you can send or schedule next.
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPreviewOpen(true)}
            disabled={values.lineItems.length === 0}
            leadingIcon={<Eye className="size-4" />}
            className="text-muted-foreground hover:text-foreground"
            title={
              values.lineItems.length === 0
                ? 'Add at least one item to preview'
                : 'Live preview of the current invoice'
            }
          >
            Preview
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          ) : null}
          <Button type="submit" loading={loading}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sub-components — kept inline because they all read the same hook scope.
// Each is small and parameterised; pulling them out would just move state
// across module boundaries without changing the per-render cost.
// ---------------------------------------------------------------------------

/**
 * Compact display-only line row. The full edit surface is the
 * `LineItemDialog`; discount and tax stay as their own dialogs
 * launched from the row pills (nested clickable elements stop
 * propagation so the row click stays unambiguous: row body →
 * line dialog, pill → that pill's dialog).
 */
function LineRow({
  idx,
  line,
  currency,
  lineTotal,
  canRemove,
  onRemove,
  onEdit,
  onOpenDiscount,
  onOpenTax,
  taxMethod,
}: {
  idx: number;
  line: InvoiceLineItemFormValues;
  currency: string;
  lineTotal: string;
  canRemove: boolean;
  onRemove: () => void;
  onEdit: () => void;
  onOpenDiscount: () => void;
  onOpenTax: () => void;
  taxMethod: InvoiceFormValues['tax']['method'];
}) {
  const hasLineTax =
    taxMethod === 'per_item' &&
    line.taxRate !== undefined &&
    line.taxRate.trim() !== '';
  const description = line.description.trim();
  const hasDescription = description !== '';
  const qty = line.quantity || '1';
  const priceDisplay = (() => {
    const parsed = parseUserMoney(line.unitPriceInput, currency);
    if (parsed.ok) {
      return displayWireMoney(parsed.value.amount.toString(), currency);
    }
    return line.unitPriceInput || '—';
  })();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
      className="group flex flex-col gap-1.5 rounded-xl border border-line bg-card px-4 py-3 transition-colors hover:border-line-strong hover:bg-secondary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      aria-label={`Edit line ${idx + 1}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p
            className={cn(
              'truncate text-sm font-medium',
              hasDescription ? 'text-foreground' : 'italic text-muted-foreground',
            )}
          >
            {hasDescription ? description : 'Untitled item'}
          </p>
          <p className="font-mono text-xs tabular-nums text-muted-foreground">
            {qty} × {priceDisplay}
            {line.unit && line.unit !== 'fixed' ? (
              <span className="ml-1 font-sans">/ {line.unit}</span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono text-sm font-medium tabular-nums text-foreground">
            {displayWireMoney(lineTotal, currency)}
          </span>
          <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              aria-label={`Edit line ${idx + 1}`}
              className="size-7 text-muted-foreground hover:text-foreground"
            >
              <Pencil className="size-3.5" />
            </Button>
            {canRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                aria-label={`Remove line ${idx + 1}`}
                className="size-7 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="size-3.5" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {line.notes ? (
        <p className="truncate text-xs text-muted-foreground">{line.notes}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        {line.discount ? (
          <RowPill
            tone="discount"
            onClick={onOpenDiscount}
            icon={<Tag className="size-3" />}
            label={formatLineDiscount(line.discount, currency)}
          />
        ) : (
          <RowPill
            tone="ghost"
            onClick={onOpenDiscount}
            icon={<Plus className="size-3" />}
            label="Discount"
          />
        )}
        {/* Per-line tax pill only shows when the invoice is in
            `none` or `per_item` mode. Under `on_total` /
            `as_deduction` every line is taxable; there's no
            per-row affordance to surface. */}
        {taxMethod === 'on_total' || taxMethod === 'as_deduction' ? null : hasLineTax ? (
          <RowPill
            tone="tax"
            onClick={onOpenTax}
            icon={<Percent className="size-3" />}
            label={`${line.taxRate}%`}
          />
        ) : (
          <RowPill
            tone="ghost"
            onClick={onOpenTax}
            icon={<Plus className="size-3" />}
            label="Tax"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Tiny pill used for inline row affordances (discount, tax). Always
 * stops propagation so the parent row click never overlaps with the
 * pill click.
 */
function RowPill({
  tone,
  onClick,
  icon,
  label,
}: {
  tone: 'discount' | 'tax' | 'ghost';
  onClick: (e: React.MouseEvent) => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
        tone === 'discount' &&
          'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300',
        tone === 'tax' &&
          'bg-accent-soft/40 text-foreground hover:bg-accent-soft/60',
        tone === 'ghost' &&
          'text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * Render a form discount in the inline pill (the row tag, the
 * sidebar pill). For fixed amounts we lift the user-typed decimal
 * back into the wire shape so `displayWireMoney` does the locale
 * formatting; if the input is unparseable (mid-edit), fall back to
 * showing what the user typed.
 */
function formatLineDiscount(d: FormDiscount, currency: string): string {
  if (d.type === 'percentage') return `−${d.rate}%`;
  const parsed = parseUserMoney(d.amount, currency);
  if (!parsed.ok) return `−${d.amount}`;
  return `−${displayWireMoney(parsed.value.amount.toString(), currency)}`;
}

/**
 * Per-row Tax cell. Behavior depends on the invoice's current tax
 * mode (a single wire field, so we adapt the affordance instead of
 * letting the user pick a contradicting mode):
 *
 *  - `none`        → "+ Tax" button (opening starts per_item mode)
 *  - `per_item`    → pill with the row's rate, or "+ Tax" if unset
 *  - `on_total`    → checkbox: "is this line included in the invoice tax?"
 *  - `as_deduction`→ checkbox: same, but for the withholding line
 */
function BillToButton({
  client,
  emailText,
  onClick,
  invalid,
}: {
  client: Client | null;
  emailText: string | null;
  onClick: () => void;
  invalid: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-invalid={invalid}
      className={cn(
        'flex items-center justify-between gap-3 rounded-xl border bg-card px-3 py-2.5 text-left transition-colors hover:border-line-strong',
        client ? 'border-accent/40 bg-accent-soft/30' : 'border-dashed border-line',
        invalid && 'border-destructive/60',
      )}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        {client ? (
          <>
            <ClientAvatar
              name={client.name}
              photoUrl={client.photoUrl ?? null}
              tag={inferClientTag({ name: client.name, company: client.company })}
              size={32}
            />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-foreground">
                {client.displayName || client.name}
              </span>
              {emailText ? (
                <span className="truncate text-xs text-muted-foreground">{emailText}</span>
              ) : null}
            </span>
          </>
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <User2 className="size-4" /> Pick a client
          </span>
        )}
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/**
 * Slim, single-line date strip: `Issue 2026-05-13 · Due 2026-05-27`
 * + Net chips. Lives directly under the bill-to row in the header —
 * dates are secondary information (defaults are usually fine) so
 * they get a quiet inline treatment rather than the prominent
 * stacked-label layout the previous version used.
 */
function DateStrip({
  issueDate,
  dueDate,
  onIssueChange,
  onDueChange,
  issueError,
  dueError,
}: {
  issueDate: string;
  dueDate: string;
  onIssueChange: (next: string) => void;
  onDueChange: (next: string) => void;
  issueError?: string;
  dueError?: string;
}) {
  function applyNet(days: number) {
    if (!issueDate) return;
    const issue = new Date(issueDate);
    if (Number.isNaN(issue.getTime())) return;
    const due = new Date(issue);
    due.setDate(due.getDate() + days);
    const yyyy = due.getFullYear();
    const mm = String(due.getMonth() + 1).padStart(2, '0');
    const dd = String(due.getDate()).padStart(2, '0');
    onDueChange(`${yyyy}-${mm}-${dd}`);
  }
  const netDays = (() => {
    if (!issueDate || !dueDate) return null;
    const i = new Date(issueDate).getTime();
    const d = new Date(dueDate).getTime();
    if (Number.isNaN(i) || Number.isNaN(d)) return null;
    return Math.round((d - i) / (1000 * 60 * 60 * 24));
  })();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
        <Label htmlFor="invoice-issue-date" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          <Calendar className="size-3" /> Issue
        </Label>
        <Input
          id="invoice-issue-date"
          type="date"
          value={issueDate}
          onChange={(e) => onIssueChange(e.target.value)}
          className="h-8 w-auto px-2 text-sm"
        />
        <span className="text-muted-foreground/40">·</span>
        <Label htmlFor="invoice-due-date" className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Due
        </Label>
        <Input
          id="invoice-due-date"
          type="date"
          value={dueDate}
          onChange={(e) => onDueChange(e.target.value)}
          className="h-8 w-auto px-2 text-sm"
        />
        <span className="text-muted-foreground/40">·</span>
        <div className="flex items-center gap-1">
          {[7, 14, 30].map((days) => {
            const on = netDays === days;
            return (
              <button
                key={days}
                type="button"
                onClick={() => applyNet(days)}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
                  on
                    ? 'bg-foreground text-background'
                    : 'bg-card text-muted-foreground hover:bg-secondary hover:text-foreground',
                )}
              >
                Net {days}
              </button>
            );
          })}
        </div>
      </div>
      {issueError || dueError ? (
        <p className="text-[11px] text-destructive">{issueError ?? dueError}</p>
      ) : null}
    </div>
  );
}

function NotesField({
  id,
  label,
  hint,
  register,
}: {
  id: string;
  label: string;
  hint?: string;
  register: ReturnType<ReturnType<typeof useForm<InvoiceFormValues>>['register']>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label
        htmlFor={id}
        className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
      >
        {label}
      </Label>
      <Textarea
        id={id}
        rows={2}
        className="resize-none"
        placeholder={hint}
        {...register}
      />
    </div>
  );
}

function InlineField({
  label,
  required,
  error,
  children,
  className,
}: {
  label: string;
  value?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
        {required ? (
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </span>
      {children}
      {error ? (
        <span className="text-[11px] text-destructive">{error}</span>
      ) : null}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-mono text-sm tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function DiscountSummaryRow({
  discount,
  discountTotal,
  currency,
  onOpen,
  onClear,
}: {
  discount: FormDiscount | undefined;
  discountTotal: string;
  currency: string;
  onOpen: () => void;
  onClear: () => void;
}) {
  // When no invoice-level discount is set but line discounts exist,
  // `discountTotal` is still non-zero (per-line discounts feed into
  // the same aggregate). Show the running total but only expose the
  // invoice-level "+ Add discount" affordance.
  const totalIsPositive = discountTotal !== '0';
  if (discount) {
    return (
      <div className="flex items-baseline justify-between gap-3">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline dark:text-emerald-300"
        >
          <Percent className="size-3" />
          {discount.type === 'percentage' ? `${discount.rate}% off` : 'Discount'}
        </button>
        <div className="flex items-center gap-1">
          <span className="font-mono text-sm tabular-nums text-foreground">
            −{displayWireMoney(discountTotal, currency)}
          </span>
          <button
            type="button"
            onClick={onClear}
            aria-label="Remove invoice discount"
            className="rounded p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-3">
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        <Plus className="size-3" /> Add discount
      </button>
      <span className="font-mono text-sm tabular-nums text-foreground">
        {totalIsPositive ? `−${displayWireMoney(discountTotal, currency)}` : displayWireMoney(discountTotal, currency)}
      </span>
    </div>
  );
}

function TaxSummaryRow({
  tax,
  taxTotal,
  currency,
  onOpen,
  onClear,
}: {
  tax: InvoiceFormValues['tax'];
  taxTotal: string;
  currency: string;
  onOpen: () => void;
  onClear: () => void;
}) {
  // Per-item mode: the rate lives on each row. Show the running total
  // but don't expose an invoice-level edit affordance (would be a
  // mutex conflict).
  if (tax.method === 'per_item') {
    return (
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          Tax <span className="text-[10px] uppercase tracking-wider">· per-item</span>
        </span>
        <span className="font-mono text-sm tabular-nums text-foreground">
          {displayWireMoney(taxTotal, currency)}
        </span>
      </div>
    );
  }
  if (tax.method === 'on_total' || tax.method === 'as_deduction') {
    const isDeduction = tax.method === 'as_deduction';
    return (
      <div className="flex items-baseline justify-between gap-3">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1 text-xs text-foreground hover:underline"
        >
          {isDeduction ? <ArrowDownToLine className="size-3" /> : <Percent className="size-3" />}
          {tax.label ?? 'Tax'} {tax.rate}%
        </button>
        <div className="flex items-center gap-1">
          <span className="font-mono text-sm tabular-nums text-foreground">
            {isDeduction ? '−' : ''}{displayWireMoney(taxTotal, currency)}
          </span>
          <button
            type="button"
            onClick={onClear}
            aria-label="Remove invoice tax"
            className="rounded p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </div>
      </div>
    );
  }
  // method === 'none'
  return (
    <div className="flex items-baseline justify-between gap-3">
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        <Plus className="size-3" /> Add tax
      </button>
      <span className="font-mono text-sm tabular-nums text-foreground">
        {displayWireMoney(taxTotal, currency)}
      </span>
    </div>
  );
}

/**
 * Seed the line-tax dialog from the form state. For per-line tax we
 * only care about rate + the invoice-level label (the dialog reuses
 * it as the placeholder so users see consistent labelling).
 */
function lineTaxToTaxValue(
  values: InvoiceFormValues,
  idx: number,
): TaxValue | null {
  const line = values.lineItems[idx];
  if (!line || !line.taxRate || line.taxRate.trim() === '') return null;
  const label = 'label' in values.tax ? values.tax.label ?? 'Tax' : 'Tax';
  return { rate: line.taxRate, label, kind: 'add' };
}

function invoiceTaxToTaxValue(tax: InvoiceFormValues['tax']): TaxValue | null {
  if (tax.method === 'on_total') {
    return { rate: tax.rate, label: tax.label ?? 'Tax', kind: 'add' };
  }
  if (tax.method === 'as_deduction') {
    return { rate: tax.rate, label: tax.label ?? 'Withholding', kind: 'subtract' };
  }
  return null;
}

function PaymentMethodsSummary({
  methods,
  onOpen,
}: {
  methods: InvoiceFormValues['paymentMethods'];
  onOpen: () => void;
}) {
  if (!methods || methods.length === 0) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        <CreditCard className="size-3.5" /> Add payment methods
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Payment methods
      </p>
      <div className="flex flex-wrap gap-1.5">
        {methods.map((m) => (
          <span
            key={m.id}
            className="inline-flex items-center gap-1 rounded-full bg-accent-soft/40 px-2 py-0.5 text-[11px] font-medium text-foreground"
          >
            {m.label || defaultPaymentMethodLabel(m.id)}
          </span>
        ))}
        <button
          type="button"
          onClick={onOpen}
          className="rounded-full px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

interface LiveTotals {
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  total: string;
  perLine: string[];
}

/**
 * Live preview of invoice totals computed via the same domain engine
 * the server uses, so the screen number == the wire number once the
 * mutation lands. Each line price is parsed via parseUserMoney; an
 * unparseable line is treated as zero for the preview (the form
 * itself flags the bad input on submit).
 */
function useLiveTotals(values: InvoiceFormValues): LiveTotals {
  return useMemo(() => {
    const currency = values.currency || 'USD';
    const lineItems: LineItemInput[] = values.lineItems.map((line, idx) => {
      const parsed = parseUserMoney(line.unitPriceInput, currency);
      const unitPrice = parsed.ok ? parsed.value : Money.zero(currency);
      const qtyNum = Number(line.quantity);
      const quantity =
        Number.isFinite(qtyNum) && qtyNum > 0 ? line.quantity || '1' : '1';
      const lineDiscount = formDiscountToDomain(line.discount, currency);
      const taxRate =
        line.taxRate && /^[0-9]+(?:[.,][0-9]+)?$/.test(line.taxRate.trim())
          ? line.taxRate.replace(',', '.')
          : undefined;
      return {
        id: line.id ?? `line-${idx}`,
        quantity,
        unitPrice,
        taxable: line.taxable,
        ...(lineDiscount ? { discount: lineDiscount } : {}),
        ...(taxRate ? { taxRate } : {}),
      };
    });
    const invoiceDiscount = formDiscountToDomain(values.invoiceDiscount, currency);
    try {
      const taxConfig = formTaxToWire(values.tax);
      const result = computeInvoiceTotals({
        currency,
        tax: taxConfig as never,
        lineItems,
        ...(invoiceDiscount ? { invoiceDiscount } : {}),
      });
      const perLine = lineItems.map((li) => {
        try {
          // line total = unit * qty (no tax — that's in the totals box).
          return Money.mul(li.unitPrice, li.quantity).amount.toString();
        } catch {
          return '0';
        }
      });
      return {
        subtotal: result.subtotal.amount.toString(),
        discountTotal: result.discountTotal.amount.toString(),
        taxTotal: result.tax.total.amount.toString(),
        total: result.total.amount.toString(),
        perLine,
      };
    } catch {
      const zero = Money.zero(currency).amount.toString();
      void getCurrencyScale(currency);
      return {
        subtotal: zero,
        discountTotal: zero,
        taxTotal: zero,
        total: zero,
        perLine: lineItems.map(() => zero),
      };
    }
  }, [values]);
}

/**
 * Lift a form discount into the shape the domain engine wants. For
 * fixed amounts we parse the user's decimal via `parseUserMoney`;
 * if the input is mid-edit (unparseable), return `undefined` so the
 * preview falls back to "no discount" rather than zeroing the whole
 * line.
 */
function formDiscountToDomain(
  d: FormDiscount | undefined,
  currency: string,
): DiscountConfig | undefined {
  if (!d) return undefined;
  if (d.type === 'percentage') {
    if (!/^[0-9]+(?:[.,][0-9]+)?$/.test(d.rate.trim())) return undefined;
    return { type: 'percentage', rate: d.rate.replace(',', '.') };
  }
  const parsed = parseUserMoney(d.amount, currency);
  if (!parsed.ok) return undefined;
  return { type: 'fixed', amount: parsed.value };
}
